import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    ArrowLeft, TrendingUp, TrendingDown, Minus, Calendar, CheckCircle2,
    XCircle, Clock, AlertTriangle, Target, BarChart3, MessageSquare, Flame
} from 'lucide-react'
import { mockMentorStudents, mockPendingReports, type MentorStudent } from '@/data/mentor-mock-data'

interface StudentDetailViewProps {
    studentId: string
    onBack: () => void
}

export function StudentDetailView({ studentId, onBack }: StudentDetailViewProps) {
    const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'analytics'>('overview')

    const student = mockMentorStudents.find(s => s.id === studentId)
    const studentReports = mockPendingReports.filter(r => r.studentId === studentId)

    if (!student) {
        return (
            <div className="text-center py-12">
                <p className="text-theme-secondary">Student not found</p>
                <button onClick={onBack} className="mt-4 text-primary-blue hover:underline">
                    ← Go Back
                </button>
            </div>
        )
    }

    const getTrendIcon = () => {
        if (student.performanceTrend === 'improving') return <TrendingUp className="h-4 w-4 text-green-600" />
        if (student.performanceTrend === 'declining') return <TrendingDown className="h-4 w-4 text-red-600" />
        return <Minus className="h-4 w-4 text-gray-600" />
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header with Back Button */}
            <div className="flex items-center gap-4">
                <button
                    onClick={onBack}
                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                    <ArrowLeft className="h-5 w-5 text-theme-primary" />
                </button>
                <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">{student.name}</h2>
                    <p className="text-sm text-theme-secondary">
                        {student.batch} • Target: {student.targetYear}
                    </p>
                </div>
                <button className="px-4 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Message
                </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Flame className="h-4 w-4 text-orange-500" />
                        <p className="text-xs text-theme-secondary">Current Streak</p>
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{student.currentStreak} days</p>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-blue-500" />
                        <p className="text-xs text-theme-secondary">Avg Hours/Day</p>
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{student.todayHoursReported || 0}h</p>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                        <Target className="h-4 w-4 text-purple-500" />
                        <p className="text-xs text-theme-secondary">Discipline Score</p>
                    </div>
                    <p className="text-2xl font-bold text-theme-primary">{student.disciplineScore}/10</p>
                </GlassmorphicCard>

                <GlassmorphicCard className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                        {getTrendIcon()}
                        <p className="text-xs text-theme-secondary">Performance</p>
                    </div>
                    <p className={`text-sm font-semibold capitalize ${student.performanceTrend === 'improving' ? 'text-green-600' :
                        student.performanceTrend === 'declining' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                        {student.performanceTrend}
                    </p>
                </GlassmorphicCard>
            </div>

            {/* Status Alert */}
            {(student.dropRisk === 'high' || student.disciplineScore < 6 || !student.todayHoursSubmitted) && (
                <GlassmorphicCard className={`p-4 border-l-4 ${student.dropRisk === 'high' ? 'border-l-red-500' : 'border-l-amber-500'
                    }`}>
                    <div className="flex items-start gap-3">
                        <AlertTriangle className={`h-5 w-5 shrink-0 ${student.dropRisk === 'high' ? 'text-red-600' : 'text-amber-600'
                            }`} />
                        <div className="flex-1">
                            <h3 className="font-semibold text-theme-primary mb-1">Needs Attention</h3>
                            <ul className="text-sm text-theme-secondary space-y-1">
                                {student.dropRisk === 'high' && <li>• High drop-out risk detected</li>}
                                {student.disciplineScore < 6 && <li>• Low discipline score ({student.disciplineScore}/10)</li>}
                                {!student.todayHoursSubmitted && <li>• No hours submitted today</li>}
                                {student.currentStreak === 0 && <li>• Streak broken - needs motivation</li>}
                            </ul>
                        </div>
                    </div>
                </GlassmorphicCard>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'overview'
                        ? 'text-primary-blue border-b-2 border-primary-blue'
                        : 'text-theme-secondary hover:text-theme-primary'
                        }`}
                >
                    Overview
                </button>
                <button
                    onClick={() => setActiveTab('timeline')}
                    className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'timeline'
                        ? 'text-primary-blue border-b-2 border-primary-blue'
                        : 'text-theme-secondary hover:text-theme-primary'
                        }`}
                >
                    Timeline
                </button>
                <button
                    onClick={() => setActiveTab('analytics')}
                    className={`px-4 py-2 font-semibold transition-colors ${activeTab === 'analytics'
                        ? 'text-primary-blue border-b-2 border-primary-blue'
                        : 'text-theme-secondary hover:text-theme-primary'
                        }`}
                >
                    Analytics
                </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <OverviewTab student={student} />}
            {activeTab === 'timeline' && <TimelineTab studentReports={studentReports} />}
            {activeTab === 'analytics' && <AnalyticsTab student={student} />}
        </div>
    )
}

function OverviewTab({ student }: { student: MentorStudent }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Weak Subjects */}
            <GlassmorphicCard className="p-6">
                <h3 className="font-bold text-theme-primary mb-4">Weak Subjects</h3>
                <div className="space-y-3">
                    {student.weakSubjects.map((subject) => (
                        <div key={subject} className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
                            <span className="text-sm font-medium text-theme-primary">{subject}</span>
                            <span className="text-xs text-red-600 font-semibold">Needs Focus</span>
                        </div>
                    ))}
                    {student.weakSubjects.length === 0 && (
                        <p className="text-sm text-theme-secondary text-center py-4">No weak subjects identified</p>
                    )}
                </div>
            </GlassmorphicCard>

            {/* Activity Summary */}
            <GlassmorphicCard className="p-6">
                <h3 className="font-bold text-theme-primary mb-4">Recent Activity</h3>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-theme-secondary">Last Active</span>
                        <span className="text-sm font-semibold text-theme-primary">{student.lastActivity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-theme-secondary">Today's Submission</span>
                        <span className={`text-sm font-semibold ${student.todayHoursSubmitted ? 'text-green-600' : 'text-red-600'}`}>
                            {student.todayHoursSubmitted ? '✓ Submitted' : '✗ Pending'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-theme-secondary">Best Streak</span>
                        <span className="text-sm font-semibold text-theme-primary">{student.currentStreak} days</span>
                    </div>
                </div>
            </GlassmorphicCard>
        </div>
    )
}

function TimelineTab({ studentReports }: { studentReports: any[] }) {
    return (
        <div className="space-y-3">
            {studentReports.length === 0 && (
                <GlassmorphicCard className="p-12 text-center">
                    <Calendar className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <p className="text-theme-secondary">No reports available yet</p>
                </GlassmorphicCard>
            )}

            {studentReports.map((report) => (
                <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <GlassmorphicCard className="p-4">
                        <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${report.approvalStatus === 'approved' ? 'bg-green-100 dark:bg-green-900/30' :
                                report.approvalStatus === 'rejected' ? 'bg-red-100 dark:bg-red-900/30' :
                                    'bg-amber-100 dark:bg-amber-900/30'
                                }`}>
                                {report.approvalStatus === 'approved' ? <CheckCircle2 className="h-5 w-5 text-green-600" /> :
                                    report.approvalStatus === 'rejected' ? <XCircle className="h-5 w-5 text-red-600" /> :
                                        <Clock className="h-5 w-5 text-amber-600" />}
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-theme-primary">{report.date}</h4>
                                    <span className="text-sm font-semibold text-theme-primary">{report.reportedHours}h</span>
                                </div>
                                <div className="flex gap-4 text-xs text-theme-secondary mb-2">
                                    <span>📘 Physics: {report.subjectBreakdown.physics}h</span>
                                    <span>🧪 Chemistry: {report.subjectBreakdown.chemistry}h</span>
                                    <span>🧬 Biology: {report.subjectBreakdown.biology}h</span>
                                </div>
                                <p className="text-sm text-theme-secondary italic">"{report.workDescription}"</p>
                                {report.mentorFeedback && (
                                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                                        <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">Mentor Feedback:</p>
                                        <p className="text-sm text-theme-primary">{report.mentorFeedback}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </GlassmorphicCard>
                </motion.div>
            ))}
        </div>
    )
}

function AnalyticsTab({ student }: { student: MentorStudent }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GlassmorphicCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-theme-primary">Study Consistency</h3>
                    <BarChart3 className="h-5 w-5 text-theme-secondary" />
                </div>
                <div className="space-y-4">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-theme-secondary">Discipline Score</span>
                            <span className="text-sm font-semibold text-theme-primary">{student.disciplineScore}/10</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full ${student.disciplineScore >= 8 ? 'bg-green-500' :
                                    student.disciplineScore >= 6 ? 'bg-amber-500' : 'bg-red-500'
                                    }`}
                                style={{ width: `${student.disciplineScore * 10}%` }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-theme-secondary">Avg Hours/Day</span>
                            <span className="text-sm font-semibold text-theme-primary">{student.todayHoursReported || 0}h / 8h</span>
                        </div>
                        <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-primary-blue rounded-full"
                                style={{ width: `${Math.min((student.todayHoursReported || 0) / 8, 1) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>
            </GlassmorphicCard>

            <GlassmorphicCard className="p-6">
                <h3 className="font-bold text-theme-primary mb-4">Risk Assessment</h3>
                <div className="space-y-3">
                    <div className={`p-3 rounded-lg ${student.dropRisk === 'high' ? 'bg-red-50 dark:bg-red-900/10' :
                        student.dropRisk === 'medium' ? 'bg-amber-50 dark:bg-amber-900/10' :
                            'bg-green-50 dark:bg-green-900/10'
                        }`}>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-theme-primary">Drop-out Risk</span>
                            <span className={`text-xs font-semibold uppercase ${student.dropRisk === 'high' ? 'text-red-600' :
                                student.dropRisk === 'medium' ? 'text-amber-600' : 'text-green-600'
                                }`}>
                                {student.dropRisk}
                            </span>
                        </div>
                    </div>
                    <div className="text-sm text-theme-secondary space-y-2">
                        <p>• Current streak: {student.currentStreak} days</p>
                        <p>• Performance trend: {student.performanceTrend}</p>
                    </div>
                </div>
            </GlassmorphicCard>
        </div>
    )
}
